import type { ReactElement, ReactNode } from 'react';
import { AlertTriangle, Link2Off, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import type { DeleteImpactDto, DeletePersonRef } from './api';

interface DeleteImpactSummaryProps {
  readonly impact: DeleteImpactDto;
}

/** One "N things" line with a leading glyph; omitted entirely when the count is 0. */
function ImpactLine({
  count,
  icon,
  label,
}: {
  readonly count: number;
  readonly icon: ReactNode;
  readonly label: string;
}): ReactElement | null {
  if (count <= 0) return null;
  return (
    <li className="flex items-center gap-2">
      <span className="shrink-0 text-muted-foreground" aria-hidden="true">
        {icon}
      </span>
      <span className="tabular-nums">{label}</span>
    </li>
  );
}

/**
 * A compact, named roster of the people a section affects (the backend resolves the names). Rendered
 * under the counts so the operator sees *who* is deleted / unlinked, not just how many. Patients are
 * badged so a clinical record is never mistaken for a pedigree-only individual.
 */
function PersonList({
  heading,
  persons,
  patientBadge,
  columns = 1,
  maxHeightClass = 'max-h-40',
}: {
  readonly heading: string;
  readonly persons: readonly DeletePersonRef[];
  readonly patientBadge: string;
  /** 1 = stacked single column (default); 3 = a 3-column grid for compact, wide rosters. */
  readonly columns?: 1 | 3;
  /** Tailwind max-height token bounding the scroll box; keeps a long roster from stretching the modal. */
  readonly maxHeightClass?: string;
}): ReactElement | null {
  if (persons.length === 0) return null;
  const layoutClass =
    columns === 3 ? 'grid grid-cols-3 gap-x-3 gap-y-0.5' : 'space-y-0.5';
  return (
    <div className="space-y-1 ps-1">
      <p className="text-xs font-medium text-muted-foreground">{heading}</p>
      {/* Fixed-height scroll box so a long roster doesn't push the whole modal past the viewport. */}
      <ul
        className={`${maxHeightClass} ${layoutClass} overflow-y-auto rounded-md border border-border/60 bg-muted/20 p-1.5 text-foreground/90`}
      >
        {persons.map((person) => (
          <li key={person.guid} className="flex items-center gap-1.5">
            <span aria-hidden="true" className="text-muted-foreground">
              &bull;
            </span>
            <span>{person.name}</span>
            {person.is_patient === true && (
              <span className="rounded bg-destructive/10 px-1 py-0.5 text-[10px] font-medium uppercase leading-none text-destructive">
                {patientBadge}
              </span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Presentational render of a {@link DeleteImpactDto}: the DELETE counts (pedigrees / patient records /
 * individuals / families permanently removed) and the UNLINK counts (nodes detached, families the
 * target is merely unlinked from). Counts are the backend's truth — this never estimates. When
 * nothing at all cascades (a lone target with no shared refs) it shows a plain "nothing else affected"
 * reassurance line so the operator knows the blast radius is empty.
 */
export function DeleteImpactSummary({ impact }: DeleteImpactSummaryProps): ReactElement {
  const { t } = useTranslation();

  const deleteTotal =
    impact.delete_pedigree_count +
    impact.delete_patient_count +
    impact.delete_individual_count +
    impact.delete_family_count;
  const unlinkTotal = impact.unlink_node_count + impact.unlink_family_guids.length;

  const deletedPersons = impact.deleted_persons ?? [];
  const unlinkedPersons = impact.unlinked_persons ?? [];

  return (
    <div className="space-y-3 text-sm">
      {deleteTotal > 0 && (
        <div className="space-y-1.5">
          <p className="flex items-center gap-2 font-medium text-destructive">
            <Trash2 className="h-4 w-4" aria-hidden="true" />
            {t('delete.impact.deleteHeading', { defaultValue: 'Permanently deleted' })}
          </p>
          <ul className="grid grid-cols-3 gap-x-3 gap-y-1 ps-1 text-foreground/90">
            <ImpactLine
              count={impact.delete_pedigree_count}
              icon={<Trash2 className="h-3.5 w-3.5" />}
              label={t('delete.impact.pedigrees', {
                defaultValue: '{{count}} pedigrees',
                count: impact.delete_pedigree_count,
              })}
            />
            <ImpactLine
              count={impact.delete_patient_count}
              icon={<Trash2 className="h-3.5 w-3.5" />}
              label={t('delete.impact.patients', {
                defaultValue: '{{count}} patient records',
                count: impact.delete_patient_count,
              })}
            />
            <ImpactLine
              count={impact.delete_individual_count}
              icon={<Trash2 className="h-3.5 w-3.5" />}
              label={t('delete.impact.individuals', {
                defaultValue: '{{count}} individuals',
                count: impact.delete_individual_count,
              })}
            />
            <ImpactLine
              count={impact.delete_family_count}
              icon={<Trash2 className="h-3.5 w-3.5" />}
              label={t('delete.impact.families', {
                defaultValue: '{{count}} families',
                count: impact.delete_family_count,
              })}
            />
          </ul>
          <PersonList
            heading={t('delete.impact.deletedPeopleHeading', {
              defaultValue: 'People being deleted:',
            })}
            persons={deletedPersons}
            patientBadge={t('delete.impact.patientBadge', { defaultValue: 'patient' })}
            columns={3}
            maxHeightClass="max-h-20"
          />
        </div>
      )}

      {unlinkTotal > 0 && (
        <div className="space-y-1.5">
          <p className="flex items-center gap-2 font-medium text-foreground/80">
            <Link2Off className="h-4 w-4" aria-hidden="true" />
            {t('delete.impact.unlinkHeading', { defaultValue: 'Kept, but unlinked' })}
          </p>
          <ul className="space-y-1 ps-1 text-foreground/90">
            <ImpactLine
              count={impact.unlink_node_count}
              icon={<Link2Off className="h-3.5 w-3.5" />}
              label={t('delete.impact.nodes', {
                defaultValue: '{{count}} people detached from a pedigree they share',
                count: impact.unlink_node_count,
              })}
            />
            <ImpactLine
              count={impact.unlink_family_guids.length}
              icon={<Link2Off className="h-3.5 w-3.5" />}
              label={t('delete.impact.unlinkFamilies', {
                defaultValue: '{{count}} families left intact (unlinked only)',
                count: impact.unlink_family_guids.length,
              })}
            />
          </ul>
          <PersonList
            heading={t('delete.impact.unlinkedPeopleHeading', {
              defaultValue: 'People being unlinked:',
            })}
            persons={unlinkedPersons}
            patientBadge={t('delete.impact.patientBadge', { defaultValue: 'patient' })}
          />
        </div>
      )}

      {deleteTotal === 0 && unlinkTotal === 0 && (
        <p className="flex items-center gap-2 text-muted-foreground">
          <AlertTriangle className="h-4 w-4" aria-hidden="true" />
          {t('delete.impact.nothingElse', { defaultValue: 'Nothing else is affected.' })}
        </p>
      )}
    </div>
  );
}
